/**
 * Three ways to get the form filled in.
 *
 * Form A is fourteen sections long, and almost every answer it asks for is
 * already written down in papers the applicant is holding. Making somebody copy
 * that out by hand is work the machine can do.
 *
 * What this never does is file anything. It puts a draft in the form and stops.
 * The applicant reads it, corrects it, fills in what is missing and presses
 * submit themselves.
 */

import { useRef, useState, type ReactElement } from "react";
import { bi, type Bilingual } from "@ksidc/ontology";
import { useLang, useSession } from "@ksidc/client";

export interface DraftResult {
  values: Record<string, string>;
  stillNeeded: { key: string; label: string; clause: string; section: string }[];
  filledCount: number;
  requiredCount: number;
}

interface Response {
  draft: DraftResult;
  read: { name: string; how: string; characters: number }[];
  failed: { name: string; reason: string }[];
  notice: Bilingual;
}

export function FillFromDocuments({
  onDraft,
}: {
  onDraft: (values: Record<string, string>) => void;
}): ReactElement {
  const { t, lang } = useLang();
  const { auth } = useSession();
  const docs = useRef<HTMLInputElement>(null);
  const word = useRef<HTMLInputElement>(null);

  const [busy, setBusy] = useState<null | "docs" | "word">(null);
  const [result, setResult] = useState<Response | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function send(files: FileList | null, which: "docs" | "word"): Promise<void> {
    if (!files || files.length === 0) return;
    setBusy(which);
    setError(null);
    setResult(null);
    try {
      const body = new FormData();
      for (const f of Array.from(files)) body.append("file", f);
      const token = auth ? await auth.token() : null;
      const res = await fetch("/api/assist/draft", {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body,
      });
      const json = (await res.json()) as Response & { message?: string };
      if (!res.ok) throw new Error(json.message ?? "That did not work.");
      setResult(json);
      onDraft(json.draft.values);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const filled = result?.draft.filledCount ?? 0;
  const needed = result?.draft.stillNeeded ?? [];

  return (
    <section className="panel assist">
      <h2 style={{ marginTop: 0 }}>
        {t(bi("Fill this in for me", "ഇത് എനിക്കായി പൂരിപ്പിക്കുക"))}
      </h2>
      <p>
        {t(
          bi(
            "Most of what this form asks for is already written in papers you have. Upload them and the answers will be put into the form for you to check.",
            "ഈ ഫോറം ചോദിക്കുന്ന മിക്ക കാര്യങ്ങളും നിങ്ങളുടെ കയ്യിലുള്ള രേഖകളിൽ എഴുതിയിട്ടുണ്ട്. അവ അപ്‌ലോഡ് ചെയ്താൽ, നിങ്ങൾക്ക് പരിശോധിക്കാൻ ഉത്തരങ്ങൾ ഫോറത്തിൽ ചേർക്കും.",
          ),
        )}
      </p>

      <div className="assist-ways">
        <div className="assist-way">
          <h3>{t(bi("From your documents", "നിങ്ങളുടെ രേഖകളിൽ നിന്ന്"))}</h3>
          <p className="field-help">
            {t(
              bi(
                "A project report, a land document, a partnership deed. Pictures of them are fine.",
                "പദ്ധതി റിപ്പോർട്ട്, ഭൂരേഖ, പങ്കാളിത്ത ആധാരം. അവയുടെ ഫോട്ടോ ആയാലും മതി.",
              ),
            )}
          </p>
          <input
            ref={docs}
            type="file"
            multiple
            accept="image/*,application/pdf"
            style={{ display: "none" }}
            onChange={(e) => void send(e.target.files, "docs")}
          />
          <button
            type="button"
            className="btn"
            disabled={busy !== null}
            onClick={() => docs.current?.click()}
          >
            {busy === "docs"
              ? t(bi("Reading your documents", "രേഖകൾ വായിക്കുന്നു"))
              : t(bi("Choose documents", "രേഖകൾ തിരഞ്ഞെടുക്കുക"))}
          </button>

          {/*
            * Papers for somebody who has none.
            *
            * This system is being shown to people who are not applying for
            * anything. The reading is the thing worth seeing and it cannot be
            * seen without a document to read, so three are offered here: a
            * project report, a title deed and a site plan, each saying on its
            * face that it is a stand-in. Download them, send them straight back
            * up, and the form fills itself in front of you.
            *
            * The same three the walkthrough film uploads, from the same files,
            * so what a visitor sees is what the film showed.
            */}
          <details className="samples">
            <summary>
              {t(bi("I do not have any papers to hand", "എന്റെ കയ്യിൽ രേഖകളൊന്നുമില്ല"))}
            </summary>
            <p className="field-help">
              {t(
                bi(
                  "Then try it with these. They are made up, and each one says so on its face. Download them and send them back up here to watch the form fill itself.",
                  "എങ്കിൽ ഇവ ഉപയോഗിച്ച് നോക്കുക. ഇവ സാങ്കൽപ്പികമാണ്; ഓരോന്നിലും അത് എഴുതിയിട്ടുമുണ്ട്. ഡൗൺലോഡ് ചെയ്ത് ഇവിടെ തിരികെ അയച്ചാൽ ഫോം സ്വയം പൂരിപ്പിക്കുന്നത് കാണാം.",
                ),
              )}
            </p>
            <p style={{ marginTop: "0.6rem" }}>
              <a className="btn" href="/samples/sample-papers.zip" download>
                {t(bi("Download all three", "മൂന്നും ഡൗൺലോഡ് ചെയ്യുക"))}
              </a>
            </p>
            <ul className="sample-list">
              {[
                ["project-report.pdf", bi("Project report", "പദ്ധതി റിപ്പോർട്ട്")],
                ["title-deed.pdf", bi("Title deed", "ആധാരം")],
                ["site-plan.pdf", bi("Site plan", "സൈറ്റ് പ്ലാൻ")],
              ].map(([file, label]) => (
                <li key={file as string}>
                  <a href={`/samples/${file as string}`} download>
                    {t(label as Bilingual)}
                  </a>
                </li>
              ))}
            </ul>
          </details>
        </div>

        <div className="assist-way">
          <h3>{t(bi("On paper, in Word", "വേഡിൽ, കടലാസിൽ"))}</h3>
          <p className="field-help">
            {t(
              bi(
                "Take the form away, fill it at your own pace or with your accountant, and bring it back.",
                "ഫോറം കൊണ്ടുപോയി, നിങ്ങളുടെ സൗകര്യത്തിന് അല്ലെങ്കിൽ കണക്കപ്പിള്ളയുടെ സഹായത്തോടെ പൂരിപ്പിച്ച് തിരികെ കൊണ്ടുവരിക.",
              ),
            )}
          </p>
          <div className="btn-row">
            <a className="btn btn-secondary" href="/api/assist/form-a-template.docx">
              {t(bi("Download the form", "ഫോറം ഡൗൺലോഡ് ചെയ്യുക"))}
            </a>
            <input
              ref={word}
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              style={{ display: "none" }}
              onChange={(e) => void send(e.target.files, "word")}
            />
            <button
              type="button"
              className="btn"
              disabled={busy !== null}
              onClick={() => word.current?.click()}
            >
              {busy === "word"
                ? t(bi("Reading your file", "ഫയൽ വായിക്കുന്നു"))
                : t(bi("Upload the filled form", "പൂരിപ്പിച്ച ഫോറം അപ്‌ലോഡ് ചെയ്യുക"))}
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="callout callout-alarm">
          <p style={{ marginBottom: 0 }}>{error}</p>
        </div>
      ) : null}

      {result ? (
        <div className="callout callout-calm assist-result">
          <p className="callout-title">
            {t(
              bi(
                `${filled} answers have been put into the form.`,
                `${filled} ഉത്തരങ്ങൾ ഫോറത്തിൽ ചേർത്തിട്ടുണ്ട്.`,
              ),
            )}
          </p>
          <p>{lang === "ml" ? result.notice.ml : result.notice.en}</p>

          {needed.length > 0 ? (
            <>
              <p style={{ fontWeight: 600 }}>
                {t(
                  bi(
                    `${needed.length} answers were not in your documents. Please fill these in yourself.`,
                    `${needed.length} ഉത്തരങ്ങൾ നിങ്ങളുടെ രേഖകളിൽ ഉണ്ടായിരുന്നില്ല. അവ നിങ്ങൾ തന്നെ പൂരിപ്പിക്കുക.`,
                  ),
                )}
              </p>
              <ul className="assist-needed">
                {needed.slice(0, 12).map((n) => (
                  <li key={n.key}>
                    <b>{n.label}</b> <span className="muted">{n.section}</span>
                  </li>
                ))}
                {needed.length > 12 ? (
                  <li className="muted">
                    {t(bi(`and ${needed.length - 12} more`, `കൂടാതെ ${needed.length - 12} എണ്ണം`))}
                  </li>
                ) : null}
              </ul>
            </>
          ) : (
            <p>
              {t(
                bi(
                  "Every answer the form requires was found. Read them all before you submit.",
                  "ഫോറത്തിന് ആവശ്യമായ എല്ലാ ഉത്തരങ്ങളും കണ്ടെത്തി. സമർപ്പിക്കുന്നതിന് മുൻപ് എല്ലാം വായിക്കുക.",
                ),
              )}
            </p>
          )}

          {result.failed.length > 0 ? (
            <p className="muted">
              {t(bi("Could not read: ", "വായിക്കാൻ കഴിഞ്ഞില്ല: "))}
              {result.failed.map((f) => `${f.name} (${f.reason})`).join("; ")}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
